import { useCallback, useMemo } from 'react'
import { z } from 'zod'

import { usePlaceOrderMutation } from '@shared/generated'
import { RemotePendingOrderState } from './usePendingOrder'

const placeOrderShape = z.object({
  customer_phone: z.string({ message: 'Customer phone is required' }),
  address: z.string({ message: 'Address is required' }),
  shop_id: z.number({ coerce: true }).int().nonnegative().finite(),
}).passthrough()

class PlaceResultError {
  constructor (public code: number = -1, public message: string = '') { }

  getMessage () {
    return this.message
  }

  getAppErrorCode () {
    switch (this.code) {
    case 0:
      return 'ok'
    case 1:
      return 'already_placed'
    case 2:
      return 'access_denied'
    case 3:
      return 'invalid_data'
    case 4:
      return 'invalid_payment_data'
    default:
      return 'unknown_error'
    }
  }

  isNotReady () {
    return this.code === -1
  }

  isOk () {
    return this.code === 0
  }

  isError () {
    return this.code > 0
  }
}

export const usePlaceOrder = () => {
  const [ placeOrderMutation, { data, loading } ] = usePlaceOrderMutation()

  const canPlaceOrder = useCallback((order: RemotePendingOrderState) => {
    const result = placeOrderShape.safeParse(order)

    if (!result.success) {
      return false
    }

    return true
  }, [])

  const placeOrder = useCallback(async (order: RemotePendingOrderState) => {
    if (!order) {
      return {
        orderId: null,
        isOrderPlacedSuccessfully: false,
        errorShape: new PlaceResultError(3, 'Invalid order data')
      }
    }

    if (!canPlaceOrder(order)) {
      return {
        orderId: order.id,
        isOrderPlacedSuccessfully: false,
        errorShape: new PlaceResultError(3, 'Invalid order data')
      }
    }

    const { data } = await placeOrderMutation({
      variables: {
        orderId: order.id
      },
      update: (cache, { data }) => {
        if (!data?.placeOrder.placed) return

        cache.modify({
          fields: {
            allMyOrders (existing = { edges: [] }) {
              return {
                ...existing,
                edges: [ order, ...existing.edges ],
              }
            },
          },
        })
      },
    })

    return {
      orderId: data?.placeOrder?.order_id ?? order.id,
      isOrderPlacedSuccessfully: data?.placeOrder?.placed,
      errorShape: new PlaceResultError(data?.placeOrder?.error_code ?? 0, data?.placeOrder?.message ?? ''),
    }
  }, [ canPlaceOrder, placeOrderMutation ])

  const placeOrderResult = useMemo(() => {
    return data?.placeOrder ?? null
  }, [ data ])

  const errorShape = new PlaceResultError(placeOrderResult?.error_code ?? 0, placeOrderResult?.message ?? '')

  return {
    canPlaceOrder,
    placeOrder,
    loading,
    placeOrderResult,
    isOrderPlacedSuccessfully: !!placeOrderResult?.placed,
    errorCode: placeOrderResult?.error_code,
    errorMessage: placeOrderResult?.message ?? null,
    errorShape
  }

}